import GuardianElf from "../animations/guardians/GuardianElf";
import Guardian from "../animations/guardians/Guardian";
import GuardianKnight from "../animations/guardians/GuardianKnight";
import GuardianRanger from "../animations/guardians/GuardianRanger";
import GuardianWarrior from "../animations/guardians/GuardianWarrior";
import GuardianWizard from "../animations/guardians/GuardianWizard";
import common from "../common";
import frag from "../shaders/pick.frag";
import GelButton from "./gel/GelButton";
import GelHackButton from "./gel/GelHackButton";
import GelHackToggleButton from "./gel/GelHackToggleButton";

export default class CharacterSelector extends PIXI.Container {
    constructor(mode = CharacterSelector.MODE_CHARACTER) {
        super();

        this.signals = {
            character: new P3.utils.MiniSignal(),
            item: new P3.utils.MiniSignal()
        };

        this._mode = null;
        this._frame = 0;
        this._pageIndex = 0;
        this._pageCount = 0;
        this._setPageFunc = null;

        this._characterColumns = 4;
        this._characterRows = 3;
        this._characterSlots = [];

        this._itemColumns = 4;
        this._itemRows = 5;
        this._itemSlots = [];

        this._slotMask = null;
        this._slotMaskTall = null;

        let states = new P3.ButtonStates();
        states.normal = P3.assets.texture("ui_btn_tab_l_out");
        states.over = P3.assets.texture("ui_btn_tab_l_over");
        states.down = P3.assets.texture("ui_btn_tab_l_down");
        states.normalAlt = P3.assets.texture("ui_btn_tab_l_over");
        states.overAlt = P3.assets.texture("ui_btn_tab_l_over");

        this._characterButton = new GelHackToggleButton(states);
        this._characterButton.animate = false;
        this._characterButton.animateIcon = false;
        this._characterButton.clickSoundName = "sfx_btn_press_00";
        this._characterButton.voSoundName = "vo_queen_118";
        this._characterButton.tabIndex = GelButton.TAB_INDEX_LEVEL_1;
        this._characterButton.signals.click.add(this.onCharacterButtonClick, this);
        this.addChild(this._characterButton);

        this._characterButton.label = new PIXI.extras.BitmapText("Character", {font: "30px BBC Reith Sans"});
        this._characterButton.label.position.set(0, 2);
        this._characterButton.label.anchor.set(0.5);
        this._characterButton.label.tint = 0xFFFFFF;
        this._characterButton.animation.addChild(this._characterButton.label);

        states = new P3.ButtonStates();
        states.normal = P3.assets.texture("ui_btn_tab_r_out");
        states.over = P3.assets.texture("ui_btn_tab_r_over");
        states.down = P3.assets.texture("ui_btn_tab_r_down");
        states.normalAlt = P3.assets.texture("ui_btn_tab_r_over");
        states.overAlt = P3.assets.texture("ui_btn_tab_r_over");

        this._itemButton = new GelHackToggleButton(states);
        this._itemButton.animate = false;
        this._itemButton.animateIcon = false;
        this._itemButton.clickSoundName = "sfx_btn_press_00";
        this._itemButton.voSoundName = "vo_queen_116";
        this._itemButton.tabIndex = GelButton.TAB_INDEX_LEVEL_1 + 1;
        this._itemButton.signals.click.add(this.onItemButtonClick, this);
        this.addChild(this._itemButton);

        this._itemButton.label = new PIXI.extras.BitmapText("Equipment", {font: "30px BBC Reith Sans"});
        this._itemButton.label.position.set(0, 2);
        this._itemButton.label.anchor.set(0.5);
        this._itemButton.label.tint = 0xFFFFFF;
        this._itemButton.animation.addChild(this._itemButton.label);

        this._panel = new PIXI.mesh.NineSlicePlane(P3.assets.texture("ui_library_9slice"), 40, 40, 40, 40);
        this._panel.position.set(0, 0);
        this._panel.width = 432;
        this._panel.height = 600;
        this._panel.pivot.set(this._panel.width * 0.5, this._panel.height * 0.5);
        this.addChild(this._panel);

        this._characterButton.position.set(-90, -(this._panel.height + this._characterButton.height) * 0.5 + 8);
        this._itemButton.position.set(90, -(this._panel.height + this._itemButton.height) * 0.5 + 8);

        this._activeSlots = new PIXI.Container();
        this._activeSlots.position.set(this._panel.x + 22, this._panel.y + 28);
        this._panel.addChild(this._activeSlots);

        this.initPagePanel();
        this.initSlotMasks();
        this.loadCharacters();
        this.loadItems();

        this.setMode(mode);
    }

    destroy() {
        P3.utils.destroyAllSignals(this.signals);

        super.destroy({children: true});
    }

    update(dt) {
        const rate = 3;
        if (this._frame++ % rate === 0) {
            for (let i = 0; i < this._activeSlots.children.length; ++i) {
                const slot = this._activeSlots.children[i];
                slot.portrait && slot.portrait.update && slot.portrait.update(dt * rate * 0.5);
            }
        }
    }

    initPagePanel() {
        this._pagePanel = new PIXI.Container();
        this._pagePanel.position.set(214, 0);
        this.addChild(this._pagePanel);

        this._pagePanel.image = new PIXI.Sprite(P3.assets.texture("ui_pagination_bg"));
        this._pagePanel.image.height = 300;
        this._pagePanel.addChild(this._pagePanel.image);

        this._pagePanel.blips = new PIXI.Container();
        this._pagePanel.blips.position.set(32, this._pagePanel.image.height * 0.5);
        this._pagePanel.addChild(this._pagePanel.blips);

        this._pagePanel.nextButton = GelHackButton.create(
            P3.assets.texture("ui_btn_page_down_normal"),
            P3.assets.texture("ui_btn_page_down_over"),
            P3.assets.texture("ui_btn_page_down_down")
        );
        this._pagePanel.nextButton.position.set(32, this._pagePanel.image.height);
        this._pagePanel.nextButton.animate = false;
        this._pagePanel.nextButton.animateIcon = false;
        this._pagePanel.nextButton.tabIndex = GelButton.TAB_INDEX_LEVEL_2 - 1;
        this._pagePanel.nextButton.signals.click.add(this.onPageNextButtonClick, this);
        this._pagePanel.addChild(this._pagePanel.nextButton);

        this._pagePanel.previousButton = GelHackButton.create(
            P3.assets.texture("ui_btn_page_up_normal"),
            P3.assets.texture("ui_btn_page_up_over"),
            P3.assets.texture("ui_btn_page_up_down")
        );
        this._pagePanel.previousButton.position.set(32, 0);
        this._pagePanel.previousButton.animate = false;
        this._pagePanel.previousButton.animateIcon = false;
        this._pagePanel.previousButton.tabIndex = GelButton.TAB_INDEX_LEVEL_2 - 2;
        this._pagePanel.previousButton.signals.click.add(this.onPagePreviousButtonClick, this);
        this._pagePanel.addChild(this._pagePanel.previousButton);

        this._pagePanel.pivot.set(0, this._pagePanel.image.height * 0.5);
    }

    initSlotMasks() {
        const rgb = new Float32Array(3);
        rgb[0] = 1;
        rgb[1] = 1;
        rgb[2] = 1;

        let temp = new PIXI.Sprite(P3.assets.texture("ui_library_slot_equip"));
        temp.filters = [new PIXI.Filter(null, frag, {pickColor: {type: "vec3", value: rgb}})];
        this._slotMask = common.renderer.generateTexture(temp);

        temp = new PIXI.Sprite(P3.assets.texture("ui_library_slot_character"));
        temp.filters = [new PIXI.Filter(null, frag, {pickColor: {type: "vec3", value: rgb}})];
        this._slotMaskTall = common.renderer.generateTexture(temp);
    }

    loadCharacters() {
        const data = P3.utils.objectToArray(P3.assets.json("characters")).filter(x => x.enabled);
        data.sort((a, b) => {
            return a.order - b.order;
        });

        const total = this._characterColumns * this._characterRows;

        const spacing = new PIXI.Point(96, 180);
        for (let i = 0; i < data.length; ++i) {
            const t = data[i];
            const index = i % total;

            const slot = GelHackButton.create(P3.assets.texture("ui_library_slot_character"));
            slot.id = i;
            slot.data = t;
            slot.page = Math.floor(i / total);
            slot.position.set(
                (index % this._characterColumns) * spacing.x + slot.width * 0.5,
                Math.floor(index / this._characterColumns) * spacing.y + slot.height * 0.5
            );
            slot.animate = false;
            slot.animateIcon = false;
            slot.clickSoundName = "sfx_btn_keypad_press_00";
            slot.tabIndex = GelButton.TAB_INDEX_LEVEL_2 + index;
            slot.interactive = false;
            slot.signals.click.add(this.onCharacterSlotButtonClick, this);
            this._characterSlots.push(slot);

            const golden = common.save.isWeeklyChallengeComplete(common.userId);

            if (common.config.game.character_select.animate) {
                switch (t.type) {
                    default :
                    case Guardian.TYPE_KNIGHT : {
                        slot.portrait = new GuardianKnight(golden);
                        break;
                    }
                    case Guardian.TYPE_WARRIOR : {
                        slot.portrait = new GuardianWarrior(golden);
                        break;
                    }
                    case Guardian.TYPE_ELF : {
                        slot.portrait = new GuardianElf(golden);
                        break;
                    }
                    case Guardian.TYPE_RANGER : {
                        slot.portrait = new GuardianRanger(golden);
                        break;
                    }
                    case Guardian.TYPE_WIZARD : {
                        slot.portrait = new GuardianWizard(golden);
                        break;
                    }
                }

                slot.portrait.position.set(-12, 212);
                slot.portrait.scale.set(0.3);
                slot.portrait.update(0);
                slot.portrait.init(t.skin);
                slot.addChild(slot.portrait);

                slot.portrait.playIdleAnim();

                let k = i * 10;
                while (--k > 0) slot.portrait.update(Math.random());

                slot.portrait.mask = new PIXI.Sprite(this._slotMaskTall);
                slot.portrait.mask.anchor.set(0.5);
                slot.addChild(slot.portrait.mask);
            } else {
                slot.portrait = new PIXI.Sprite(P3.assets.texture(t.images.portrait));
                slot.portrait.anchor.set(0.5);
                slot.addChild(slot.portrait);
            }

            slot.highlight = new PIXI.Sprite(P3.assets.texture("ui_library_slot_character_selected"));
            slot.highlight.anchor.set(0.5);
            slot.highlight.visible = common.character.skin === t.skin;
            slot.addChild(slot.highlight);
        }
    }

    loadItems() {
        const user = common.save.getUserData(common.userId);

        const data = P3.utils.objectToArray(P3.assets.json("equipment")).filter(x => x.enabled);
        data.sort((a, b) => {
            return a.order - b.order;
        });

        const total = this._itemColumns * this._itemRows;

        const spacing = new PIXI.Point(96, 110);
        for (let i = 0; i < data.length; ++i) {
            const t = data[i];
            const index = i % total;

            let locked = false;
            if (!common.config.game.debug_unlock_equipment && t.locked) {
                // check if we have unlocked this item
                if (!(user.unlock[t.slot] && user.unlock[t.slot].indexOf(t.type) !== -1)) {
                    locked = true;
                }
            }

            const name = Array.isArray(t.slot) ? t.slot[0] : t.slot;
            let equipped = user.equipment[name] === t.type;

            const slot = GelHackButton.create(locked ? P3.assets.texture("ui_library_slot_equip_locked") :
                P3.assets.texture("ui_library_slot_equip_unlocked"));

            slot.id = i;
            slot.slot = t.slot;
            slot.page = Math.floor(i / total);
            slot.type = t.type;
            slot.position.set(
                (index % this._itemColumns) * spacing.x + slot.width * 0.5,
                Math.floor(index / this._itemColumns) * spacing.y + slot.height * 0.5
            );
            slot.animate = false;
            slot.animateIcon = false;
            slot.clickSoundName = "sfx_btn_keypad_press_00";
            slot.tabIndex = GelButton.TAB_INDEX_LEVEL_2 + index;
            slot.interactive = false;
            slot.signals.click.add(this.onItemSlotButtonClick, this);
            this._itemSlots.push(slot);

            slot.enabled = !locked;

            slot.selected = new PIXI.Sprite(P3.assets.texture("ui_library_slot_equip_selected"));
            slot.selected.anchor.set(0.5);
            slot.selected.visible = equipped;
            slot.addChild(slot.selected);

            slot.image = new PIXI.Sprite(P3.assets.texture(t.images.default));
            slot.image.anchor.set(0.5);
            slot.image.alpha = locked ? 0.6 : 1;
            slot.addChild(slot.image);

            slot.lock = new PIXI.Sprite(P3.assets.texture("ui_equip_lock"));
            slot.lock.anchor.set(0.5);
            slot.lock.visible = locked;
            slot.addChild(slot.lock);

            slot.highlight = new PIXI.Sprite(P3.assets.texture("ui_library_slot_equip_highlight"));
            slot.highlight.anchor.set(0.5);
            slot.highlight.visible = equipped;
            slot.addChild(slot.highlight);
        }
    }

    clearSlots() {
        for (let i = 0; i < this._activeSlots.children.length; ++i) {
            const slot = this._activeSlots.getChildAt(i);
            slot.interactive = false;
        }
        this._activeSlots.removeChildren();
    }

    initPageBlips(count) {
        this._pagePanel.blips.removeChildren();

        const spacing = 200 / count;
        for (let i = 0; i < count; ++i) {
            const blip = new PIXI.Sprite(P3.assets.texture("ui_pagination_off"));
            blip.position.set(0, i * spacing - ((count - 1) * spacing) * 0.5);
            blip.anchor.set(0.5);
            this._pagePanel.blips.addChild(blip);
        }
    }

    setCharacterPage(index = 0) {
        this.clearSlots();

        for (let i = 0; i < this._characterSlots.length; ++i) {
            const slot = this._characterSlots[i];
            if (slot.page === index) {
                slot.interactive = true;
                this._activeSlots.addChild(slot);
            }
        }

        for (let i = 0; i < this._pagePanel.blips.children.length; ++i) {
            const blip = this._pagePanel.blips.children[i];
            blip.texture = i === index ? P3.assets.texture("ui_pagination_on") : P3.assets.texture("ui_pagination_off");
        }
    }

    setItemPage(index = 0) {
        this.clearSlots();

        for (let i = 0; i < this._itemSlots.length; ++i) {
            const slot = this._itemSlots[i];
            if (slot.page === index) {
                slot.interactive = true;
                this._activeSlots.addChild(slot);
            }
        }

        for (let i = 0; i < this._pagePanel.blips.children.length; ++i) {
            const blip = this._pagePanel.blips.children[i];
            blip.texture = i === index ? P3.assets.texture("ui_pagination_on") : P3.assets.texture("ui_pagination_off");
        }
    }

    setMode(mode) {
        if (mode !== this._mode) {
            this._mode = mode;
            switch (mode) {
                case CharacterSelector.MODE_CHARACTER : {
                    this._characterButton.toggle = true;
                    this._itemButton.toggle = false;

                    this._pageCount = Math.ceil(this._characterSlots.length / (this._characterColumns * this._characterRows));
                    this._setPageFunc = this.setCharacterPage;

                    this.initPageBlips(this._pageCount);
                    this.setCharacterPage(0);
                    break;
                }
                case CharacterSelector.MODE_ITEM : {
                    this._characterButton.toggle = false;
                    this._itemButton.toggle = true;

                    this._pageCount = Math.ceil(this._itemSlots.length / (this._itemColumns * this._itemRows));
                    this._setPageFunc = this.setItemPage;

                    console.log(this._itemSlots.length, (this._itemColumns * this._itemRows));

                    this.initPageBlips(this._pageCount);
                    this.setItemPage(0);
                    break;
                }
            }
        }
    }

    disableInput(value = true) {
        this._characterButton.interactive = !value;
        this._itemButton.interactive = !value;
        this._pagePanel.nextButton.interactive = !value;
        this._pagePanel.previousButton.interactive = !value;

        for (let i = 0; i < this._activeSlots.children.length; ++i) {
            const slot = this._activeSlots.getChildAt(i);
            slot.interactive = !value;
        }
    }

    gotoItemPage(slot, type) {
        this.setMode(CharacterSelector.MODE_ITEM);

        if (Array.isArray(slot)) {
            slot = slot[0];
        }

        const item = this._itemSlots.filter(x => x.slot.indexOf(slot) !== -1 && x.type === type)[0];
        if (item) {
            this.setItemPage(item.page);
        }
    }

    onCharacterButtonClick(button) {
        this.setMode(CharacterSelector.MODE_CHARACTER);
        //P3.audio.playVO("vo_queen_08");
    }

    onItemButtonClick(button) {
        this.setMode(CharacterSelector.MODE_ITEM);

        // tracking
        common.gmi.sendStatsEvent("equipment", "click");
    }

    onCharacterSlotButtonClick(button) {
        for (let i = 0; i < this._characterSlots.length; ++i) {
            const slot = this._characterSlots[i];
            slot.highlight.visible = false;
        }
        button.highlight.visible = true;

        this.signals.character.dispatch(this, button.id, button.data);
    }

    onItemSlotButtonClick(button) {
        let slotArr = button.slot;
        if (!Array.isArray(slotArr)) {
            slotArr = [slotArr];
        }

        for (let i = 0; i < this._itemSlots.length; ++i) {
            const slot = this._itemSlots[i];
            const name = Array.isArray(slot.slot) ? slot.slot[0] : slot.slot;
            if (slotArr.indexOf(name) !== -1) {
                slot.selected.visible = slot.highlight.visible = false;
            }
        }

        this.signals.item.dispatch(this, button.id, button.slot, button.type);

        const user = common.save.getUserData(common.userId);
        button.highlight.visible = user.equipment[slotArr[0]] === button.type;
        button.selected.visible = button.highlight.visible;
    }

    onPageNextButtonClick(button) {
        if (++this._pageIndex > this._pageCount - 1) {
            this._pageIndex = this._pageCount - 1;
        }
        this._setPageFunc(this._pageIndex);
    }

    onPagePreviousButtonClick(button) {
        if (--this._pageIndex < 0) {
            this._pageIndex = 0;
        }
        this._setPageFunc(this._pageIndex);
    }

    get mode() {
        return this._mode;
    }
}

CharacterSelector.MODE_CHARACTER = "character";
CharacterSelector.MODE_ITEM = "item";